import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, Truck, Lock, GraduationCap, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { euro } from "@/lib/api";
import { useProducts } from "@/hooks/useProducts";
import { PreorderCartNotice } from "@/components/site/Preorder";
import { PaymentLogos } from "./Trust";
import { SubCartOffer } from "@/components/subscription/SubPromo";
import { PregnancyNotice, useCartHasRepouss } from "./PregnancyNotice";
import { T, useLang, useItemName, productPath, Approx } from "@/lib/i18n";
import { useShipGeo, cartShipping } from "@/lib/shipGeo";

function Upsell({ items, setOpen }) {
  const { data } = useProducts({ type: "pack" });
  const { lang } = useLang();
  const nm = useItemName();
  const slugs = new Set(items.map((i) => i.slug));
  if (items.some((i) => (Array.isArray(data) ? data : []).some((p) => p.slug === i.slug))) return null;
  const cats = new Set(items.map((i) => i.category).filter(Boolean));
  const pack = (Array.isArray(data) ? data : [])
    .filter((p) => p.components?.some((c) => slugs.has(c.slug)) && p.compare_at > p.price)
    .sort((a, b) => (cats.has(b.category) - cats.has(a.category)) || (a.price - b.price))[0];
  if (!pack) return null;
  return (
    <Link to={productPath(pack.slug, lang)} onClick={() => setOpen(false)} data-testid="cart-upsell" className="my-6 flex items-center gap-4 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.06)] p-4 transition-colors hover:border-[var(--gold)]">
      <img src={pack.image || undefined} alt="" className="h-16 w-16 shrink-0 rounded-sm object-cover" />
      <span className="flex-1 text-sm">
        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]"><T>Complétez votre rituel</T></span>
        {nm(pack)} — {euro(pack.price)} <span className="text-[var(--muted)] line-through">{euro(pack.compare_at)}</span>
        <span className="block text-xs text-[var(--parchment)]/80"><T>Votre soin y est inclus · économisez </T>{euro(pack.compare_at - pack.price)}</span>
      </span>
    </Link>
  );
}

const PAIRS = {
  "shampoing-cactus": ["apres-shampoing-ricin-jojoba", "extreme-repouss"], "extreme-repouss": ["shampoing-cactus", "traitement-3-extreme-pouss"],
  "extreme-repouss-cheveux-fins": ["shampoing-cactus"], "traitement-3-extreme-pouss": ["extreme-repouss"], "apres-shampoing-ricin-jojoba": ["masque-supreme"],
  "creme-reconstructrice-cactus": ["serum-reconstructeur"], "serum-reconstructeur": ["masque-supreme"], "masque-supreme": ["apres-shampoing-ricin-jojoba"],
  "shampoing-natyrel": ["masque-demelant-natyrel"], "masque-demelant-natyrel": ["gel-definition-boucles"], "gel-definition-boucles": ["masque-demelant-natyrel"],
  "shampoing-anti-pelliculaire": ["apres-shampoing-ricin-jojoba"], "edge-control": ["extreme-repouss"],
  "huile-barbe": ["shampoing-barbe"], "shampoing-barbe": ["huile-barbe"],
  "shampoing-expert-locks": ["huile-locks"], "huile-locks": ["spray-locks"], "spray-locks": ["huile-locks"], "gel-locks": ["huile-locks"],
  "shampoing-argan": ["creme-demelante-junior"], "creme-demelante-junior": ["shampoing-argan"],
  "creme-visage-sublime-eclat": ["masque-argile"], "masque-argile": ["creme-visage-sublime-eclat"], "gel-douche-exaltant": ["creme-visage-sublime-eclat"],
  "serum-anti-taches-lumina-vg": ["creme-visage-sublime-eclat"], "extreme-repouss-voyage-50ml": ["shampoing-cactus"],
};

function CrossSell({ items }) {
  const { data } = useProducts();
  const { add } = useCart();
  const nm = useItemName();
  const inCart = new Set(items.map((i) => i.slug));
  const slug = items.flatMap((i) => PAIRS[i.slug] || []).find((s) => !inCart.has(s));
  const p = (Array.isArray(data) ? data : []).find((x) => x.slug === slug);
  if (!p) return null;
  return (
    <div data-testid="cart-crosssell" className="my-6 flex items-center gap-4 rounded-sm border border-[var(--line)] p-4">
      <img src={p.image || undefined} alt="" className="h-14 w-14 shrink-0 rounded-sm object-cover" />
      <span className="flex-1 text-sm"><span className="block text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]"><T>Souvent pris ensemble</T></span>{nm(p)} — {euro(p.price)}</span>
      <button onClick={() => add(p, 1)} data-testid="cart-crosssell-add" className="h-9 shrink-0 rounded-full bg-[var(--gold)] px-4 text-xs font-semibold text-[var(--ink)] hover:bg-[var(--gold-soft)]"><T>Ajouter</T></button>
    </div>
  );
}

function FormationReminder() {
  return (
    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/go/formation?src=panier`} target="_blank" rel="noopener" data-testid="cart-formation-reminder"
      className="mb-6 flex items-center gap-4 rounded-sm border border-[var(--line)] p-4 transition-colors duration-300 hover:border-[var(--gold)]">
      <GraduationCap className="h-8 w-8 shrink-0 text-[var(--gold)]" />
      <span className="flex-1 text-sm">
        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]"><T>Formation cheveux afro en ligne</T></span>
        <T>Après votre commande, recevez </T><b className="text-[var(--gold-soft)]">-10 %</b><T> sur la formation Méthode Ozentya.</T>
        <span className="mt-1 flex items-center gap-1 text-xs text-[var(--parchment)]/80 underline underline-offset-4">ozentyaformation.com <ArrowUpRight className="h-3 w-3" /></span>
      </span>
    </a>
  );
}

function CartLine({ item, setQty }) {
  const nm = useItemName();
  const { t } = useLang();
  return (
    <div data-testid={`cart-item-${item.slug}`} className="flex gap-4 border-b border-[var(--line)] py-5">
      <div className="h-24 w-20 shrink-0 overflow-hidden rounded-sm bg-[var(--ink-2)]">
        <img src={item.image || undefined} alt={nm(item)} className="h-full w-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex justify-between gap-3">
          <div>
            <p className="font-display text-base leading-tight">{nm(item)}</p>
            {item.box?.length ? (
              <p data-testid={`cart-box-contents-${item.slug}`} className="mt-1 text-xs leading-relaxed text-[var(--muted)]">{item.box.map((b) => `${b.qty > 1 ? `${b.qty} × ` : ""}${nm(b)}`).join(" · ")}</p>
            ) : null}
          </div>
          <button data-testid={`cart-remove-${item.slug}`} onClick={() => setQty(item.slug, 0)} aria-label={t("Retirer")} className="text-[var(--muted)] hover:text-[var(--gold)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center rounded-full border border-[var(--line)]">
            <button data-testid={`cart-decrease-${item.slug}`} onClick={() => setQty(item.slug, item.quantity - 1)} className="p-2"><Minus className="h-3 w-3" /></button>
            <span data-testid={`cart-qty-${item.slug}`} className="w-6 text-center text-sm">{item.quantity}</span>
            <button data-testid={`cart-increase-${item.slug}`} onClick={() => setQty(item.slug, item.quantity + 1)} className="p-2"><Plus className="h-3 w-3" /></button>
          </div>
          <span className="text-sm text-[var(--gold-soft)]">{euro(item.price * item.quantity)}</span>
        </div>
      </div>
    </div>
  );
}

export function CartDrawer() {
  const { items, setQty, total, open, setOpen } = useCart();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const repouss = useCartHasRepouss(items);
  const ship = cartShipping(useShipGeo(), lang, total);

  const checkout = () => {
    setOpen(false);
    nav("/commande");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" data-lenis-prevent className="flex w-full flex-col border-l border-[var(--line)] bg-[var(--ink)] p-0 text-[var(--parchment)] sm:max-w-md">
        <SheetHeader className="border-b border-[var(--line)] px-6 py-6 text-left">
          <p className="eyebrow">{t("Votre sélection")}</p>
          <SheetTitle className="font-display text-3xl italic text-[var(--parchment)]">{t("Panier")}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <p data-testid="cart-empty" className="py-16 text-center font-display text-xl italic text-[var(--muted)]">{t("Votre panier attend son premier soin.")}</p>
          ) : (
            <>
              {items.map((i) => <CartLine key={i.slug} item={i} setQty={setQty} />)}
              {repouss ? <PregnancyNotice testid="cart-pregnancy-notice" className="mt-5" /> : null}
              {lang === "en" ? null : <SubCartOffer items={items} onNavigate={() => setOpen(false)} />}
              <CrossSell items={items} />
              <Upsell items={items} setOpen={setOpen} />
              <FormationReminder />
            </>
          )}
        </div>
        <div className="border-t border-[var(--line)] px-6 py-5">
          <div className="flex justify-between text-sm text-[var(--muted)]"><span>{t("Sous-total")}</span><span data-testid="cart-subtotal">{euro(total)}</span></div>
          <div className="text-right"><Approx eur={total} /></div>
          {ship.text ? <p data-testid="cart-shipping" className={`mt-2 flex items-center gap-2 text-xs ${ship.reached ? "font-semibold text-[var(--gold-soft)]" : "text-[var(--parchment)]/85"}`}><Truck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {ship.text}</p> : null}
          {ship.progress !== undefined && items.length ? (
            <div data-testid="cart-shipping-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ship.progress * 100)} className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--line)]">
              <div className="h-full rounded-full bg-[var(--gold)] transition-[width] duration-500" style={{ width: `${Math.round(ship.progress * 100)}%` }} />
            </div>
          ) : null}
          <PreorderCartNotice items={items} />
          <button data-testid="cart-checkout-button" disabled={!items.length} onClick={checkout} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-sm font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:bg-[var(--gold-soft)] active:scale-[0.98] disabled:opacity-40">
            {t("Commander")} · {euro(total)}
          </button>
          <p data-testid="cart-secure-note" className="mt-3 flex items-center justify-center gap-2 text-center text-xs text-[var(--parchment)]/80"><Lock className="h-3.5 w-3.5 text-[var(--gold)]" /> {t("Paiement 100 % sécurisé · Aucun frais caché")}</p>
          <PaymentLogos className="mt-3 justify-center" testid="cart-payment-logos" />
        </div>
      </SheetContent>
    </Sheet>
  );
}
